import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/axios-instance";
import type { IStatus } from "@/types/data-models";

/**
 * Steps of the conversational onboarding, in order. Mirrors ONBOARDING_STEPS
 * in the backend's user model — the server owns progression, this is only for
 * rendering the rail and picking the right script.
 *
 * The deal comes first so its analysis runs while the user sets up the rest.
 */
export const ONBOARDING_STEPS = [
  "deal",
  "workspace",
  "thesis",
  "pipeline",
  "invite",
  "done",
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** Steps that appear on the progress rail (`done` is a marker, not a step). */
export const VISIBLE_STEPS = ONBOARDING_STEPS.filter(
  (s): s is Exclude<OnboardingStep, "done"> => s !== "done"
);

export const STEP_LABELS: Record<Exclude<OnboardingStep, "done">, string> = {
  deal: "First deal",
  workspace: "Workspace",
  thesis: "Thesis",
  pipeline: "Pipeline",
  invite: "Team",
};

export interface OnboardingTurn {
  role: "assistant" | "user";
  step: OnboardingStep;
  content?: string;
  payload?: Record<string, unknown>;
  at: string;
}

export interface OnboardingProgress {
  status: "not_started" | "in_progress" | "completed" | "skipped";
  current_step: OnboardingStep;
  completed_steps: OnboardingStep[];
  workspace: string | null;
  first_deal_company: string | null;
  transcript: OnboardingTurn[];
  started_on: string | null;
  completed_on: string | null;
}

/** The workspace fields the chat prefills its widgets from. */
export interface OnboardingWorkspace {
  _id: string;
  opportunity_name?: string;
  opportunity_type?: string;
  statuses: IStatus[];
  target_gics_industries: string[];
  target_gics_sub_industries: string[];
  target_stage_companies: string[];
  target_model_companies: string[];
  target_revenue_models: string[];
  target_distinctions: string[];
  geographical_location: string[];
  additional_info: string;
  min_cheque_size: number;
  max_cheque_size: number;
  currency: string;
}

export interface OnboardingState {
  status: string;
  onboarding: OnboardingProgress;
  needs_provisioning: boolean;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  company: { id: string; companyName: string } | null;
  workspace: OnboardingWorkspace | null;
}

export interface PipelineStage {
  title: string;
  colour: string;
  description: string;
  order: number;
}

export interface ParseStagesResult {
  status: string;
  /** False when the instruction wasn't a stage edit, or the model failed. */
  understood: boolean;
  stages: PipelineStage[];
  message: string;
}

export type CreateAccountResult =
  | { status: "created"; email: string }
  /** The address already has an account — sign them in to it instead. */
  | { status: "existing_user"; email: string };

export const onboardingApi = {
  /**
   * Public. Creates the Clerk identity and user record; the caller then signs
   * in with an emailed code, which is what proves the address is theirs.
   */
  createAccount: async (params: {
    first_name: string;
    last_name: string;
    email: string;
  }): Promise<CreateAccountResult> => {
    try {
      const { data } = await axiosInstance.post("/onboarding/account", params);
      return { status: "created", email: data.email ?? params.email };
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        return {
          status: "existing_user",
          email: error.response.data?.email ?? params.email,
        };
      }
      throw error;
    }
  },

  getState: async (): Promise<OnboardingState> => {
    const { data } = await axiosInstance.get<OnboardingState>(
      "/onboarding/state"
    );
    return data;
  },

  /** Idempotent — safe to call on every mount. */
  provision: async (): Promise<{
    company: { id: string; companyName: string };
    workspace: OnboardingWorkspace;
    onboarding: OnboardingProgress;
  }> => {
    const { data } = await axiosInstance.post("/onboarding/provision", {});
    return data;
  },

  completeStep: async (params: {
    step: Exclude<OnboardingStep, "done">;
    turns?: Array<{
      role: "assistant" | "user";
      content?: string;
      payload?: Record<string, unknown>;
    }>;
    first_deal_company?: string;
  }): Promise<{ onboarding: OnboardingProgress }> => {
    const { data } = await axiosInstance.post("/onboarding/step", params);
    return data;
  },

  finish: async (skipped = false): Promise<{ onboarding: OnboardingProgress }> => {
    const { data } = await axiosInstance.post("/onboarding/finish", { skipped });
    return data;
  },

  parseStages: async (
    instruction: string,
    stages: PipelineStage[]
  ): Promise<ParseStagesResult> => {
    const { data } = await axiosInstance.post<ParseStagesResult>(
      "/onboarding/stages/parse",
      { instruction, stages }
    );
    return data;
  },
};
